import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import LeadCampaignFormSelector from '../LeadCampaignFormSelector';
import LeadDetailsLeadList from '../LeadDetailsLeadList';
import LeadDetailsFormPanel from '../LeadDetailsFormPanel';
import useInterveneSx from '../../../hooks/useInterveneSx';
import { hasRenderableLeadFormFields } from '../../../utils/leadFormFields';

export default function LeadDetailsTab({
  phone,
  isDrawerOpen,
  organisationId,
  leadFormContext: ctx,
}) {
  const styles = useInterveneSx().contextDrawer;
  const context = ctx || {};
  const [screen, setScreen] = useState('list');
  const [pendingDiscardAction, setPendingDiscardAction] = useState('');

  const {
    leadCampaign,
    canCreateLead,
    canEditLead,
    canAssignLead,
    leadDetails,
    dynamicFormData,
    fieldErrors,
    formEnable,
    formDataLoading,
    isLoading,
    phoneError,
    hasCampaignContext,
    listRefreshToken,
    isBlankCreateMode,
    isFormDirty,
    formPhone,
    handlePhoneChange,
    applyCampaignFormSelection,
    selectLeadFromList,
    handleFieldChange,
    saveForms,
    editForms,
    createLead,
    discardFormChanges,
  } = context;

  const campaignId = leadCampaign?.campaign?.proposal_id || '';
  const leadFormId = leadCampaign?.webForm?.leads_form_id || '';
  const selectedLeadId = leadDetails?.id || leadDetails?.lead_id || '';
  const selectedLeadLabel = leadDetails?.lead_id || leadDetails?.id || '';
  const hasFormFields = useMemo(
    () => hasRenderableLeadFormFields(dynamicFormData),
    [dynamicFormData]
  );
  const isCreatingLead = isBlankCreateMode || !selectedLeadId;

  useEffect(() => {
    setScreen('list');
    setPendingDiscardAction('');
  }, [campaignId, leadFormId, phone]);

  const handleLeadSelect = useCallback((lead) => {
    selectLeadFromList?.(lead);
    setScreen('form');
  }, [selectLeadFromList]);

  const handleCreateLead = useCallback(() => {
    createLead?.();
    setScreen('form');
  }, [createLead]);

  const completeBackToList = useCallback(() => {
    discardFormChanges?.();
    setScreen('list');
  }, [discardFormChanges]);

  const completeCancelEdit = useCallback(() => {
    if (isCreatingLead) {
      discardFormChanges?.();
      setScreen('list');
      return;
    }
    discardFormChanges?.();
  }, [discardFormChanges, isCreatingLead]);

  const requestDiscardAction = useCallback((action) => {
    if (!isFormDirty) {
      if (action === 'back') completeBackToList();
      else completeCancelEdit();
      return;
    }
    setPendingDiscardAction(action);
  }, [completeBackToList, completeCancelEdit, isFormDirty]);

  const confirmDiscard = useCallback(() => {
    if (pendingDiscardAction === 'back') completeBackToList();
    else completeCancelEdit();
    setPendingDiscardAction('');
  }, [completeBackToList, completeCancelEdit, pendingDiscardAction]);

  if (!ctx) return null;

  return (
    <Box sx={styles.leadDetailsTab}>
      {screen === 'list' && (
        <LeadCampaignFormSelector
          selectedCampaign={leadCampaign?.campaign || null}
          selectedForm={leadCampaign?.webForm || null}
          onApply={applyCampaignFormSelection}
          organisationId={organisationId}
        />
      )}

      {!hasCampaignContext ? (
        <Box sx={styles.leadListEmpty}>
          <Typography variant="body2" color="text.secondary">
            Select a campaign and lead form above to view or create leads for this customer.
          </Typography>
        </Box>
      ) : (
        <Box sx={styles.leadDetailsTabBody}>
          {screen === 'list' ? (
            <LeadDetailsLeadList
              phone={phone}
              campaignId={campaignId}
              leadFormId={leadFormId}
              enabled={isDrawerOpen}
              selectedLeadId={selectedLeadId}
              refreshToken={listRefreshToken}
              canCreateLead={canCreateLead}
              onLeadSelect={handleLeadSelect}
              onCreateLead={handleCreateLead}
            />
          ) : (
            <LeadDetailsFormPanel
              phone={formPhone}
              dynamicFormData={dynamicFormData}
              fieldErrors={fieldErrors}
              phoneError={phoneError}
              formEnable={formEnable}
              formDataLoading={formDataLoading}
              isLoading={isLoading}
              onFieldChange={handleFieldChange}
              onPhoneChange={handlePhoneChange}
              onSave={saveForms}
              onEdit={editForms}
              canEditLead={canEditLead}
              onBack={() => requestDiscardAction('back')}
              onCancel={() => requestDiscardAction('cancel')}
              hasFormFields={hasFormFields}
              isCreatingLead={isCreatingLead}
              campaignId={campaignId}
              selectedLead={leadDetails}
              selectedLeadId={selectedLeadLabel}
              canAssignLead={canAssignLead}
            />
          )}
        </Box>
      )}

      <Dialog
        open={Boolean(pendingDiscardAction)}
        onClose={() => setPendingDiscardAction('')}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your changes to this lead have not been saved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDiscardAction('')}>
            Keep editing
          </Button>
          <Button color="error" variant="contained" onClick={confirmDiscard}>
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
